import { useId, type ReactNode } from "react";

/**
 * One labelled row on a detail screen: a small label above, a value below.
 *
 * Two shapes, deliberately not one. Given `onClick` it is a button and the
 * whole row is the target; otherwise it is a plain container for a control
 * that handles its own input. Nesting a real control inside a button is
 * invalid and swallows its events, so the choice is structural.
 */
type Props = {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  /** Trailing control, e.g. a clear button. Only for the non-button shape. */
  action?: ReactNode;
  /**
   * `plain` drops the card chrome and keeps only the label.
   *
   * A detail row *is* the surface it sits on, so it carries a border and a
   * background. A form field is a label above a control that already has its
   * own border — nesting the two draws a box inside a box. Same concept, one
   * labelled control, so it is a variant here rather than a second primitive.
   */
  variant?: "row" | "plain";
};

const ROW = "flex w-full items-center gap-3 rounded-card border border-line bg-surface px-4 py-3 text-left";

export function Field({ label, children, onClick, action, variant = "row" }: Props) {
  const labelId = useId();

  return (
    <div className="mb-3">
      <p id={labelId} className="mb-1 px-1 text-meta text-muted">
        {label}
      </p>
      {variant === "plain" ? (
        children
      ) : onClick ? (
        // Labelled by the visible label *and* its own contents, so the control
        // announces as "Tilldela, Elliot" rather than just "Elliot".
        <button
          type="button"
          onClick={onClick}
          aria-labelledby={`${labelId} ${labelId}-value`}
          className={ROW}
        >
          <span id={`${labelId}-value`} className="flex min-w-0 flex-1 items-center gap-3">
            {children}
          </span>
        </button>
      ) : (
        <div className={ROW}>
          {children}
          {action}
        </div>
      )}
    </div>
  );
}
